import React from "react";
import { CACHE_CONSTANTS } from "../../constants/cacheConstants";
import {
  isDesignerReviewBrand,
  getOwnStoreBrandPrefixes,
  STORE_LOCATOR_PRIORITY_RADIUS_MILES,
} from "../../constants/brandFeatures";
import {
  ElectronicsIcon, TabletsIcon, SmartPhonesIcon, SmartWatchesIcon, VideoGameConsolesIcon,
  TripleUpArrowIcon, DoubleUpArrowIcon, UpArrowIcon, StoreIcon, LaptopIcon, LedTvIcon,
  PortableSpeakersIcon, VideoGameSystemsIcon, HeadphonesIcon, EarbudsIcon,
  VideoGameControllersIcon, CarWoofersIcon, VRDevicesIcon, DesktopsIcon,
  GoldIcon, SilverIcon, PlatinumIcon, JewelryIcon, ToolsIcon,
  ToolsDrillsIcon, ToolsSawsIcon, ToolsWrenchesIcon, ToolsGrindersIcon, ToolsGardenIcon,
  ToolsChainsawsIcon, ToolsLeafBlowersIcon, ToolsPressureWashersIcon, ToolsLawnMowersIcon,
  ToolsCordedIcon, ToolsCordlessIcon, ToolsGasIcon, ToolsPneumaticIcon,
} from "../../assets/Icons";
import { IIconBasicProps } from "../../assets/Icons/IIconBasicProps";
import {
  Item, Category, Attribute, Store, StoreOption,
  Condition, SelectedAttribute, QuotationDetails,
} from "./ez-quote.types";
import { getCoordinatesFromZip, calculateDistance } from "../../utilities/helpers/locationUtils";
import type { Translations } from "../../locales/types";
import type { StoreCountry } from "../../locales";
import toolsCategoryDisplayNamesJson from "../../constants/toolsCategoryDisplayNames.json";

// ---------- Constants ----------
export const MAX_NEAREST_STORES = 5;

export const LOCAL_STORAGE_KEY = CACHE_CONSTANTS.CACHE_KEYS.EZQUOTE_STATE;

export let storesData: any = null;

export const formatEstimate = (n: number) =>
  Math.floor(n).toLocaleString("en-US");

// Maps a condition id ("A" | "B" | "C", as sent to/from the quotation APIs — see
// itemCondition in useQuoteHandlers.ts) to its translation key. Shared by the live
// flow's condition step (ez-quote.tsx conditions array) and SavedQuoteView.
export const CONDITION_LABEL_KEYS = {
  A: "bestCondition",
  B: "goodCondition",
  C: "fairCondition",
} as const;

// Internal/synthetic attribute names the frontend sends when generating a quote
// (see the `attribute` array built in useQuoteHandlers.ts) that GET /quotation/:ezQuoteId
// echoes back verbatim but that were never meant to be shown to the customer —
// fixed signal values ("Gold Color" is always "Y", "Scrap or not scrap" is always
// "Retail"), or routing metadata ("rootCategoryName"). "hasDiamond" is handled
// separately in mapQuotationToItems (reconstructed as a "Diamonds" attribute,
// matching how the live flow already displays it) rather than hidden outright.
export const HIDDEN_ATTRIBUTE_NAMES = new Set(["rootCategoryName", "Gold Color", "Scrap or not scrap", "hasDiamond"]);

// The free-text notes field on gold/jewelry items is sent under the literal name
// "Additional Details" (see useFetchHandlers.ts's synthesized gold attribute and
// AttributesStep.tsx's textAreaAttributeKeys) — rendered as a note by ItemCard,
// not a label/value row.
export const NOTE_ATTRIBUTE_NAMES = new Set(["Additional Details"]);

// Recursively searches a fetched /categories tree (the same `categories`
// state the live flow already fetches) for a node matching predicate.
const findCategoryInTree = (categories: Category[], predicate: (c: Category) => boolean): Category | null => {
  for (const cat of categories) {
    if (predicate(cat)) return cat;
    const found = findCategoryInTree(cat.childCategories || [], predicate);
    if (found) return found;
  }
  return null;
};

// Model Number is the one attribute useQuoteHandlers.ts sends as its raw
// "<modelNumber>__<modelName>" id (value.id) instead of the already-formatted
// display name every other attribute sends (value.name) — see the
// key == "Model Number" special-case in its attribute builder — so it alone
// needs reconstructing here. Replicates the exact split (dropdown.tsx) and
// word-by-word title-case (useItemHandlers.ts's handleAttributeSelect)
// applied when the value was first selected in the live flow.
const formatModelNumberValue = (rawValue: string): string => {
  const [, modelNameRaw] = rawValue.split("__");
  const modelName = modelNameRaw?.trim() || rawValue;
  return modelName
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

// Maps a saved quotation (GET /quotation/:ezQuoteId response) into the Item[]
// shape ItemCard/FinalReviewStep already render, so the saved-quote view can
// reuse that same UI instead of a parallel implementation. `categories` is the
// same tree the live flow fetches via fetchCategories — used here to resolve
// icon_name/goldParentCategory from real API data instead of a duplicated
// name-to-icon table. Verified against a real response (2026-09-09).
export const mapQuotationToItems = (quote: QuotationDetails, t: Translations, categories: Category[]): Item[] =>
  quote.itemList.map((qItem, index) => {
    const attrs = qItem.attribute || [];
    const attrMap: Record<string, string> = {};
    attrs.forEach((a) => {
      if (a.value != null) attrMap[a.name] = a.value;
    });

    const isJewelryItem = attrMap.rootCategoryName === "Jewelry" || !!attrMap["Metal Type"];

    const conditionKey = qItem.itemCondition
      ? CONDITION_LABEL_KEYS[qItem.itemCondition as keyof typeof CONDITION_LABEL_KEYS]
      : undefined;
    const condition: Condition | null = conditionKey
      ? { id: qItem.itemCondition as string, title: t.ezquote[conditionKey], description: "", icon_name: "" }
      : null;

    const estimate =
      qItem.minQuote != null && qItem.maxQuote != null
        ? `$${formatEstimate(qItem.minQuote)} - $${formatEstimate(qItem.maxQuote)}`
        : "N/A";

    const attributes: { [key: string]: SelectedAttribute } = {};
    const textAreaAttributeKeys: string[] = [];
    attrs.forEach((a) => {
      if (!a.value || HIDDEN_ATTRIBUTE_NAMES.has(a.name) || a.name === "Metal Type") return;
      if (NOTE_ATTRIBUTE_NAMES.has(a.name)) textAreaAttributeKeys.push(a.name);
      const displayValue = a.name === "Model Number" ? formatModelNumberValue(a.value) : a.value;
      attributes[a.name] = { id: a.value, name: displayValue };
    });
    // "hasDiamond" is derived/sent to the API instead of the raw "Diamonds" answer
    // (see useQuoteHandlers.ts) — reconstruct it so the existing diamond-disclaimer
    // logic in FinalReviewStep (which keys off an "attributes" entry containing
    // "diamond") behaves the same as it does for a freshly-created item.
    if (attrMap.hasDiamond !== undefined) {
      const isYes = attrMap.hasDiamond === "true";
      attributes.Diamonds = { id: isYes ? "yes" : "no", name: isYes ? "Yes" : "No" };
    }

    // icon_name resolved from the real /categories tree (matched by id, then
    // name) instead of a hardcoded duplicate table — same source of truth
    // CategoryStep uses for the live flow.
    const matchedCategory =
      findCategoryInTree(categories, (c) => c.category_id === qItem.categoryID) ??
      findCategoryInTree(categories, (c) => c.category_name === qItem.categoryName);

    const category: Category = {
      category_id: qItem.categoryID ?? 0,
      category_name: qItem.categoryName,
      icon_name: matchedCategory?.icon_name ?? null,
      is_leaf: true,
      childCategories: [],
    };

    // For jewelry, ItemCard resolves its icon from goldParentCategory (the
    // Gold/Silver/Platinum node), same as handleCategoryPress sets it for a
    // freshly-picked item — so reconstruct it from the same tree by metal name.
    const goldParentCategory = isJewelryItem
      ? findCategoryInTree(categories, (c) => c.category_name === attrMap["Metal Type"]) ?? undefined
      : undefined;

    return {
      id: `${qItem.categoryName}-${index}`,
      category,
      attributes,
      condition,
      estimate,
      textAreaAttributeKeys,
      parentCategoryName: isJewelryItem ? "Jewelry" : undefined,
      goldTypeName: isJewelryItem ? attrMap["Metal Type"] : undefined,
      itemTypeLabel: isJewelryItem ? qItem.categoryName : undefined,
      rootCategoryName: attrMap.rootCategoryName,
      goldParentCategory,
    };
  });

// Maps a saved quotation's own store (the single store GET /quotation/:ezQuoteId
// returns — the one the backend assigned to this quote, not a zip-derived search
// result) into the single-entry StoreOption[] shape FinalReviewStep's store-list
// card already renders, so the saved-quote page can show that real store without
// re-deriving a "nearby stores" list the API doesn't give us enough data to match.
export const mapQuotationStoreToOptions = (quote: QuotationDetails): StoreOption[] => {
  const store = quote.store;
  const address = store?.address;
  if (!store || !address) return [];

  return [{
    key: store.storeEzid || quote.ezQuoteId,
    value: store.storeEzid || quote.ezQuoteId,
    storeNumber: store.storeEzid || "",
    lat: 0,
    lng: 0,
    address: [address.addressLine1, address.addressLine2, address.city, address.stateAlternateText, address.zip]
      .filter(Boolean)
      .join(", "),
    storename: store.storeBrand || "",
    brandName: store.storeBrand || "",
    distance: null,
    phoneNumber: address.phone,
  }];
};

/** Maps API attribute names to user-facing display names (translation-aware). */
export const getAttrDisplayName = (name: string, t?: Translations): string => {
  if (t) return t.attributes.displayName(name);
  return name === "Quality" ? "Purity" : name;
};

/**
 * Returns header title and subtitle based on the current internal step.
 */
export const renderHeaderText = (currentStep: number, t: Translations) => {
  switch (currentStep) {
    case 2:
      return { title: t.headers.step2Title, subtitle: t.headers.step2Subtitle };
    case 3:
      return { title: t.headers.step3Title, subtitle: t.headers.step3Subtitle };
    case 4:
      return { title: t.headers.step4Title, subtitle: t.headers.step4Subtitle };
    case 5:
      return { title: t.headers.step5Title, subtitle: t.headers.step5Subtitle };
    case 6:
      return { title: t.headers.step6Title, subtitle: t.headers.step6Subtitle };
    default:
      return { title: "", subtitle: "" };
  }
};

export const resolveJewelryHeader = (
  goldParentCategory: Category | null,
  currentStep: number,
  attributes: Attribute[],
  goldCurrentAttributeIndex: number,
  t: Translations,
  brand: string | null = null
): { title: string; subtitle: string } | null => {
  if (goldParentCategory && currentStep === 2)
    return { title: t.headers.goldNarrowTitle, subtitle: t.headers.goldNarrowSubtitle };

  if (currentStep !== 3 || !attributes.some((a) => a.displayType === "SLIDER"))
    return null;

  const cur = attributes[goldCurrentAttributeIndex];
  if (!cur || cur.displayType === "SLIDER")
    return { title: t.headers.goldWeightTitle, subtitle: t.headers.goldWeightSubtitle };
  if (cur.coreAttributeName.toLowerCase().includes("karat"))
    return { title: t.headers.goldKaratTitle, subtitle: t.headers.goldKaratSubtitle };
  if (cur.coreAttributeName === "Quality")
    return { title: t.headers.goldPurityTitle, subtitle: t.headers.goldPuritySubtitle };
  if (cur.displayType === "TEXT_AREA")
    return isDesignerReviewBrand(brand)
      ? { title: t.headers.goldBrandedTitle, subtitle: t.headers.goldBrandedSubtitle }
      : { title: t.headers.goldDetailsTitle, subtitle: t.headers.goldDetailsSubtitle };
  if ((cur.values ?? []).every((v) => ["yes", "no"].includes(v.coreValue.toLowerCase())))
    return { title: t.headers.goldDiamondsTitle, subtitle: t.headers.goldDiamondsSubtitle };
  return null;
};

// Icon map
export const iconMap: { [key: string]: React.FC<IIconBasicProps> } = {
  "ic_electronics.svg": ElectronicsIcon,
  "ic_videogameconsoles.svg": VideoGameConsolesIcon,
  "ic_tablets.svg": TabletsIcon,
  "ic_smartphones.svg": SmartPhonesIcon,
  "ic_smartwatches.svg": SmartWatchesIcon,
  "triple_uparrow.svg": TripleUpArrowIcon,
  "double_uparrow.svg": DoubleUpArrowIcon,
  "uparrow.svg": UpArrowIcon,
  "stores.svg": StoreIcon,
  "ic_laptops.svg": LaptopIcon,
  "ic_digital_led_tvs.svg": LedTvIcon,
  "ic_portablespeakers.svg": PortableSpeakersIcon,
  "ic_portable_video_game_systems.svg": VideoGameSystemsIcon,
  "ic_headphones.svg": HeadphonesIcon,
  "ic_earbuds.svg": EarbudsIcon,
  "ic_portable_video_game_controllers.svg": VideoGameControllersIcon,
  "ic_car_woofers.svg": CarWoofersIcon,
  "ic_virtual_reality_devices.svg": VRDevicesIcon,
  "ic_desktops.svg": DesktopsIcon,
  "ic_gold.svg": GoldIcon,
  "ic_silver.svg": SilverIcon,
  "ic_platinum.svg": PlatinumIcon,
  "ic_jewelry.svg": JewelryIcon,
  "ic_tools.svg": ToolsIcon,
  "ic_tools_drills.svg": ToolsDrillsIcon,
  "ic_tools_saws.svg": ToolsSawsIcon,
  "ic_tools_wrenches.svg": ToolsWrenchesIcon,
  "ic_tools_grinders.svg": ToolsGrindersIcon,
  "ic_tools_garden.svg": ToolsGardenIcon,
  "ic_tools_chainsaws.svg": ToolsChainsawsIcon,
  "ic_tools_leaf_blowers.svg": ToolsLeafBlowersIcon,
  "ic_tools_pressure_washers.svg": ToolsPressureWashersIcon,
  "ic_tools_lawn_mowers.svg": ToolsLawnMowersIcon,
  "ic_tools_corded.svg": ToolsCordedIcon,
  "ic_tools_cordless.svg": ToolsCordlessIcon,
  "ic_tools_gas.svg": ToolsGasIcon,
  "ic_tools_pneumatic.svg": ToolsPneumaticIcon,
  default: ElectronicsIcon,
};

// LW-1529: Tools categories show a customer-friendly name from the bundled
// mapping (keyed by category_id, so Electronics/Jewelry ids never match). Falls
// back to the server's category_name when the id is unmapped or the active
// locale has no text. Display-only — the API payload keeps category_name.
const toolsCategoryDisplayNames: Record<string, { display_category_name: Partial<Record<Translations["locale"], string>> }> =
  toolsCategoryDisplayNamesJson;

export const getCategoryDisplayName = (
  category: Pick<Category, "category_id" | "category_name">,
  t: Translations
): string =>
  toolsCategoryDisplayNames[String(category.category_id)]?.display_category_name[t.locale] ||
  category.category_name;

// ---------- getIconComponent Function ----------
export const getIconComponent = (iconName: string | null): React.FC<IIconBasicProps> =>
  iconName && iconMap[iconName] ? iconMap[iconName] : iconMap.default;

export const getStoreCardMessage = (item: Item, t: Translations): string => {
  const hasOtherSelected = Object.values(item.attributes).some(
    (attr) => attr.id === "other"
  );

  if (hasOtherSelected) {
    const attributeNames = Object.keys(item.attributes).filter(
      (key) => item.attributes[key].id === "other"
    );
    const displayNames = attributeNames.map((k) => t.attributes.displayName(k)).join(", ");
    return t.itemCard.storeCardOtherMessage(displayNames);
  }
  return t.itemCard.storeCardDefaultMessage;
};

// Jewelry items are the only ones tagged with a parent category (see
// mapQuotationToItems / AttributesStep), so compare against it explicitly.
export const isJewelryItem = (item: Pick<Item, "parentCategoryName">): boolean =>
  item.parentCategoryName === "Jewelry";

// Intl.ListFormat is missing on older browsers still in browserslist (e.g. iOS
// Safari < 14.5) — fall back to a comma-separated list rather than throwing.
const formatList = (values: string[], locale: Translations["locale"]): string =>
  typeof Intl.ListFormat === "function"
    ? new Intl.ListFormat(locale, { type: "conjunction" }).format(values)
    : values.join(", ");

// LW-1500/LW-1536: the legal-approved copy is a single summary-level message
// (FinalReviewStep's top banner), not a per-item message — the per-item card
// above keeps its own generic/other-attribute copy unchanged. When every
// unpriced item is general merchandise, the GM message names their categories
// ("your Smartphones and Hammer Drill", opening with "these"/"the items"
// when more than one item is unpriced); otherwise more than one unpriced item
// shows the multi-item message and a single Jewelry item the Jewelry message.
export const getExpertReviewMessage = (items: Item[], t: Translations): string => {
  const naItems = items.filter((item) => item.estimate === "N/A");
  if (!naItems.some(isJewelryItem)) {
    const categoryNames = [...new Set(naItems.map((item) => getCategoryDisplayName(item.category, t)))];
    return t.finalReview.expertReviewGeneralMessage(formatList(categoryNames, t.locale), naItems.length);
  }
  return naItems.length > 1
    ? t.finalReview.expertReviewMultipleMessage
    : t.finalReview.expertReviewJewelryMessage;
};

/**
 * Helper to map internal steps (1-6) to header steps (1-4)
 * Mapping:
 *  - Internal 1 & 2 → Header Step 1
 *  - Internal 3    → Header Step 2
 *  - Internal 4    → Header Step 3
 *  - Internal 5 & 6 → Header Step 4
 */
export const getHeaderStep = (internalStep: number): number => {
  if (internalStep <= 2) return 1;
  if (internalStep === 3) return 2;
  if (internalStep === 4) return 3;
  if (internalStep >= 5) return 4;
  return internalStep;
};

export async function validateZipAndGetStores(
  zipCode: string,
  allStores: Store[],
  brand?: string | null,
  country: StoreCountry = "US"
): Promise<{ isValid: boolean; nearbyStores: StoreOption[]; selectedStore: StoreOption | null }> {
  if (!zipCode || !allStores || allStores.length === 0) {
    return { isValid: false, nearbyStores: [], selectedStore: null };
  }
  try {
    const coords = await getCoordinatesFromZip(zipCode, country);
    if (!coords) return { isValid: false, nearbyStores: [], selectedStore: null };

    // deptId is the store id the quote is sent to; skip stores without one
    // (including any already saved in the browser before they were filtered).
    // Only the widget's country (US, or MX for the Spanish brands) — a browser
    // may still hold stores from the other country from an earlier sync.
    const storesWithDistance = allStores.filter((store) => store.deptId && store.countryName === country).map((store) => {
      const brandPrefix = store.brandName ? store.brandName.split(" (")[0] : "";
      return {
        ...store,
        distance: calculateDistance(coords.lat, coords.lng, store.latitude, store.longitude),
        brandPrefix,
      };
    });

    // Sort a group zip-match-first, then nearest — same ordering the list always used.
    const sortGroup = (group: typeof storesWithDistance) => {
      const matchingZip = group.filter(s => s.zipCode === zipCode).sort((a, b) => a.distance - b.distance);
      const others = group.filter(s => s.zipCode !== zipCode).sort((a, b) => a.distance - b.distance);
      return [...matchingZip, ...others];
    };

    // If the widget's brand has its own stores within the priority radius, those sort
    // first; the list is still filled out to MAX_NEAREST_STORES with the nearest stores
    // of any brand, so nearbyStores always carries a full list, priority-ordered.
    const ownBrandPrefixes = getOwnStoreBrandPrefixes(brand);
    const isPriorityStore = (store: (typeof storesWithDistance)[number]) =>
      ownBrandPrefixes.length > 0 &&
      store.distance <= STORE_LOCATOR_PRIORITY_RADIUS_MILES &&
      ownBrandPrefixes.includes(store.brandPrefix.toLowerCase().trim());

    const priorityStores: typeof storesWithDistance = [];
    const otherStores: typeof storesWithDistance = [];
    for (const store of storesWithDistance) {
      (isPriorityStore(store) ? priorityStores : otherStores).push(store);
    }
    const prioritized = [...sortGroup(priorityStores), ...sortGroup(otherStores)].slice(0, MAX_NEAREST_STORES);

    const nearbyStores: StoreOption[] = prioritized.map((store) => {
      const brandName = store.brandName ? store.brandPrefix : "ezpawn";
      const distance = store.distance ? store.distance.toFixed(1) : null;
      const showDistance = distance && parseFloat(distance) > 0;
      return {
        key: store.id,
        value: `${store.addressLine1} ${store.city} ${store.stateAlternateText} ${store.zipCode} - ${store.name} - ${distance ?? "?"} miles`,
        storeNumber: store.deptId,
        lat: store.latitude,
        lng: store.longitude,
        address: `${store.addressLine1} ${store.city} ${store.stateAlternateText} ${store.zipCode}`,
        storename: `${store.name} - ${distance ?? "?"} miles`,
        brandName,
        distance: showDistance ? `${distance} miles` : null,
        phoneNumber: store.phoneNumber,
      };
    });

    return { isValid: true, nearbyStores, selectedStore: nearbyStores[0] ?? null };
  } catch (error) {
    console.error("Error validating ZIP code:", error);
    return { isValid: false, nearbyStores: [], selectedStore: null };
  }
}
